<template>
    <div>
        <b-alert variant="danger" :show="errorMsg.length">{{ errorMsg }}</b-alert>

        <b-container fluid>
            <b-row>
                <b-col sm="6">
                    <pay-period @errors="handleErrors"></pay-period>
                </b-col>

                <b-col sm="4">
                    <manage-paid-time-off @errors="handleErrors"></manage-paid-time-off>

                    <div class="mt-4">
                        <h5>Calendar</h5>
                        <b-list-group flush>
                            <b-list-group-item
                                v-for="employee in calendarEmployees"
                                :key="employee._id"
                                class="d-flex justify-content-between align-items-center"
                            >
                                <span>{{ employee.username }}</span>
                                <b-button size="sm" variant="outline-secondary" @click="openCalendarModal(employee.username)">
                                    View Calendar
                                </b-button>
                            </b-list-group-item>
                            <b-list-group-item v-if="!calendarEmployees.length">
                                No employees configured yet.
                            </b-list-group-item>
                        </b-list-group>
                    </div>
                </b-col>
            </b-row>
        </b-container>

        <b-modal
            v-model="showCalendarModal"
            :title="selectedCalendarUser ? `Calendar - ${selectedCalendarUser}` : 'Calendar'"
            size="lg"
            scrollable
            hide-footer
            centered
            @hidden="resetCalendarModal"
        >
            <div v-if="calendarLoading" class="text-center my-3">
                <b-spinner class="align-middle"></b-spinner>
                <strong class="mx-3">Loading...</strong>
            </div>
            <div v-else>
                <p v-if="!calendarEntries.length" class="mb-0">
                    No calendar entries for this user.
                </p>
                <b-table
                    v-else
                    small
                    :items="calendarEntries"
                    :fields="calendarFields"
                    responsive="sm"
                    head-variant="light"
                    :tbody-tr-class="calendarRowClass"
                >
                    <template #cell(date)="data">
                        {{ data.value }}
                    </template>
                    <template #cell(title)="data">
                        {{ data.value }}
                    </template>
                </b-table>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import PayPeriod from "../components/employees/PayPeriod";
    import ManagePaidTimeOff from "../components/employees/ManagePaidTimeOff";
    import timecardApi from "@/api/timecard";
    import calendarApi from "@/api/calendar";
    import moment from "moment";

    export default {
        name: "Employees",
        mixins: [ timecardApi, calendarApi ],
        components: { PayPeriod, ManagePaidTimeOff },
        data() {
            return {
                errorMsg: '',
                totals: {},
                calendarEmployees: [],
                calendarEntries: [],
                calendarFields: [
                    { key: 'date', label: 'Date' },
                    { key: 'title', label: 'Entry' },
                ],
                showCalendarModal: false,
                selectedCalendarUser: null,
                calendarLoading: false,
            };
        },
        async created() {
            await this.fetchCalendarEmployees();
        },
        methods: {
            handleErrors(error) {
                this.errorMsg = error;
            },
            async fetchCalendarEmployees() {
                try {
                    // Build the calendar list from calendar.getEmployees(), which returns usernames
                    const response = await this.getEmployees();
                    // Handle different response formats: array, object with usernames property, etc.
                    let employees = [];
                    if (Array.isArray(response)) {
                        employees = response;
                    } else if (response && Array.isArray(response.usernames)) {
                        employees = response.usernames;
                    } else if (response && response.data && Array.isArray(response.data)) {
                        employees = response.data;
                    } else if (response && response.data && Array.isArray(response.data.usernames)) {
                        employees = response.data.usernames;
                    }
                    
                    this.calendarEmployees = employees.map((emp, index) => {
                        const username = typeof emp === 'string' ? emp : (emp.username || emp);
                        return {
                            _id: `emp-${index}-${username}`,
                            username,
                        };
                    });
                } catch (err) {
                    const msg = err.response && err.response.data && err.response.data.message
                        ? err.response.data.message
                        : err.message;
                    this.errorMsg = msg;
                    // eslint-disable-next-line no-console
                    console.log('Error fetching calendar employees:', err);
                    this.calendarEmployees = [];
                }
            },
            async openCalendarModal(username) {
                this.selectedCalendarUser = username;
                this.calendarEntries = [];
                this.calendarLoading = true;
                this.showCalendarModal = true;

                try {
                    const events = await this.getEventsForEmployee(username);

                    this.calendarEntries = events
                        .map(event => {
                            const entry = {
                                date: moment(event.start).format('M/D/YYYY'),
                                title: event.title,
                                wasRemoved: !!event.wasRemoved,
                            };
                            // Store the original event for debugging
                            entry._wasRemoved = event.wasRemoved;
                            return entry;
                        })
                        .sort((a, b) => {
                            const aDate = moment(a.date, 'M/D/YYYY').toDate();
                            const bDate = moment(b.date, 'M/D/YYYY').toDate();
                            // Descending order: newest first
                            return bDate - aDate;
                        });
                    
                    // Debug: log entries with wasRemoved
                    // eslint-disable-next-line no-console
                    console.log('Calendar entries:', this.calendarEntries.filter(e => e.wasRemoved));
                } catch (err) {
                    const msg = err.response && err.response.data && err.response.data.message
                        ? err.response.data.message
                        : err.message;
                    this.errorMsg = msg;
                    // eslint-disable-next-line no-console
                    console.log(err);
                } finally {
                    this.calendarLoading = false;
                }
            },
            resetCalendarModal() {
                this.selectedCalendarUser = null;
                this.calendarEntries = [];
                this.calendarLoading = false;
                this.showCalendarModal = false;
            },
            calendarRowClass(item, type) {
                if (!item || type !== 'row') return '';
                // Check if wasRemoved is true
                if (item.wasRemoved === true || item.wasRemoved === 'true') {
                    return 'calendar-removed';
                }
                return '';
            },
        }
    }
</script>

<style>
    .calendar-removed {
        background-color: #f8d7da !important;
    }
    .calendar-removed td {
        background-color: #f8d7da !important;
    }
    .calendar-removed:hover td {
        background-color: #f5c6cb !important;
    }
</style>